import { useDispatch, useSelector } from "react-redux";
import BanIcon from "../../../../../components/icons/BanIcon";
import DeleteActionIcon from "../../../../../components/icons/DeleteActionIcon";
import { showConfirmationModal } from "../../../../../features/confirmationModal/ConfirmationModalSlice";
import { getLocalizedTime } from "../../../../../utils/timeZone";
import styles from "./oneSquad.module.css";

const OneSquad = ({
  squadId,
  name,
  slug,
  region,
  privacy,
  banned,
  membersCount,
  maxMembers,
  members = [],
  squadImagePath,
  createdAt,
  updatedAt,
}) => {
  const dispatch = useDispatch();
  const { timeZone } = useSelector((state) => state.timeZone);

  const localizedCreatedAt = createdAt
    ? getLocalizedTime(createdAt, timeZone).toLocaleString()
    : "-";
  const localizedUpdatedAt = updatedAt
    ? getLocalizedTime(updatedAt, timeZone).toLocaleString()
    : "-";

  const privacyLabel =
    privacy === "inviteOnly"
      ? "دعوة فقط"
      : privacy === "request"
        ? "طلب"
        : privacy === "public"
          ? "عام"
          : "-";
  const roleLabel = {
    captain: "صاحب السكواد",
    coLeader: "مساعد السكواد",
    member: "عضو السكواد",
  };

  const handleDeleteSquad = () => {
    dispatch(
      showConfirmationModal({
        message: "هل انت متأكد من حذف السكواد؟",
        data: squadId,
        actionType: "deleteSquad",
      }),
    );
  };

  const handleBanToggle = () => {
    dispatch(
      showConfirmationModal({
        message: banned ? "هل تريد ازالة حظر السكواد؟" : "هل تريد حظر السكواد؟",
        data: {
          squadId,
          squadData: {
            banned: !banned,
          },
        },
        actionType: "updateSquad",
      }),
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.contentWrapper}>
        <div className={styles.identityRow}>
          <div className={styles.squadImageWrapper}>
            {squadImagePath ? (
              <img
                src={squadImagePath}
                alt={name}
                className={styles.squadImage}
              />
            ) : (
              <div className={styles.squadImageFallback}>
                {(name || "?").charAt(0)}
              </div>
            )}
          </div>
          <div className={styles.identityText}>
            <h3 className={styles.squadName}>{name || "-"}</h3>
            <p className={styles.squadSlug}>{slug ? `@${slug}` : "-"}</p>
          </div>
        </div>

        <div className={styles.summaryGrid}>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>المنطقة</span>
            <p className={styles.summaryValue}>{region || "-"}</p>
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>الخصوصية</span>
            <p className={styles.summaryValue}>{privacyLabel}</p>
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>عدد الأعضاء</span>
            <p className={styles.summaryValue}>
              {membersCount ?? 0} / {maxMembers ?? 0}
            </p>
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>الحالة</span>
            <p
              className={`${styles.summaryValue} ${banned ? styles.statusDanger : styles.statusSuccess}`}
            >
              {banned ? "محظور" : "نشط"}
            </p>
          </div>
        </div>

        <div className={styles.membersSection}>
          <span className={styles.sectionLabel}>أعضاء السكواد</span>
          {members.length ? (
            <div className={styles.membersGrid}>
              {members.map((member) => {
                const memberName = member?.userId?.name || "-";
                const memberEmail = member?.userId?.email || "-";
                const memberRegion = member?.userId?.region || "-";
                const isMemberBanned = Boolean(member?.userId?.banned);

                return (
                  <div className={styles.memberCard} key={member?._id}>
                    <div className={styles.memberIdentity}>
                      {member?.userId?.userImage?.path ? (
                        <img
                          src={member?.userId?.userImage?.path}
                          alt={memberName}
                          className={styles.memberImage}
                        />
                      ) : (
                        <div className={styles.memberImageFallback}>
                          {memberName.charAt(0)}
                        </div>
                      )}
                      <div className={styles.memberText}>
                        <p className={styles.memberName}>{memberName}</p>
                        <p className={styles.memberEmail}>{memberEmail}</p>
                      </div>
                    </div>
                    <div className={styles.memberMeta}>
                      <span className={`${styles.metaBadge} ${styles.roleBadge}`}>
                        {roleLabel[member?.role] || "عضو السكواد"}
                      </span>
                      <span
                        className={`${styles.metaBadge} ${styles.neutralBadge}`}
                      >
                        المنطقة: {memberRegion}
                      </span>
                      <span
                        className={`${styles.metaBadge} ${
                          isMemberBanned ? styles.dangerBadge : styles.successBadge
                        }`}
                      >
                        {isMemberBanned ? "محظور" : "غير محظور"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={styles.emptyMembers}>لا يوجد أعضاء</div>
          )}
        </div>

        <div className={styles.metaGrid}>
          <div className={styles.metaCard}>
            <span className={styles.metaLabel}>آخر تعديل</span>
            <p className={styles.metaValue}>{localizedUpdatedAt}</p>
          </div>
          <div className={styles.metaCard}>
            <span className={styles.metaLabel}>تمت الإضافة</span>
            <p className={styles.metaValue}>{localizedCreatedAt}</p>
          </div>
        </div>
      </div>
      <div className={styles.optionsWrapper}>
        <button
          type="button"
          className={`${styles.actionButton} ${styles.moderationAction}`}
          onClick={handleBanToggle}
        >
          <BanIcon className={styles.svgIcon} />
          <span>{banned ? "ازالة حظر السكواد" : "حظر السكواد"}</span>
        </button>
        <button
          type="button"
          className={`${styles.actionButton} ${styles.deleteAction}`}
          onClick={handleDeleteSquad}
        >
          <DeleteActionIcon className={styles.svgIcon} />
          <span>حذف السكواد</span>
        </button>
      </div>
    </div>
  );
};

export default OneSquad;
